import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function RegisterPage() {
  const nav = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const [step, setStep] = useState("form"); // form | otp
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", esClienteHolistor: false });
  const [codigo, setCodigo] = useState("");
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState("");

  // Si ya hay sesión activa, ir directo a la app (no mostrar login)
  if (!authLoading && user) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;


  const submitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setStep("otp");
      if (data.devMode && data.otp) {
        setDevOtp(data.otp);
        toast.info("Modo dev: tu código es " + data.otp);
      } else {
        toast.success("Te enviamos un código a " + form.email);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: form.email, codigo });
      login(data.token, data.user);
      toast.success("¡Bienvenido!");
      nav("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex brand-bg text-white p-12 flex-col justify-start gap-12">
        <BrandLogo />
        <div>
          <h2 className="font-head text-3xl font-bold leading-tight">Acceso sin contraseña</h2>
          <p className="text-white/70 mt-4 max-w-sm leading-relaxed">
            Registrate una vez y entrá con un código que te enviamos por email. Simple y seguro.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8"><BrandLogo /></div>
          {step === "form" ? (
            <form onSubmit={submitForm} className="space-y-5" data-testid="register-form">
              <div>
                <h1 className="font-head text-2xl font-bold text-slate-900">Crear cuenta / Ingresar</h1>
                <p className="text-sm text-muted-foreground mt-1">Completá tus datos para recibir el código de acceso.</p>
              </div>
              <div>
                <Label className="label-tiny">Nombre y apellido</Label>
                <Input data-testid="reg-nombre" required value={form.nombre} className="mt-1.5 rounded-none"
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Email</Label>
                <Input data-testid="reg-email" type="email" required value={form.email} className="mt-1.5 rounded-none"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Teléfono</Label>
                <Input data-testid="reg-telefono" required value={form.telefono} className="mt-1.5 rounded-none"
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              </div>
              <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                <Checkbox data-testid="reg-cliente" checked={form.esClienteHolistor}
                  onCheckedChange={(v) => setForm({ ...form, esClienteHolistor: !!v })} />
                ¿Sos cliente de Holistor?
              </label>
              <Button type="submit" disabled={loading} data-testid="reg-submit"
                className="w-full rounded-none brand-bg text-white hover:opacity-90 h-11">
                {loading ? "Enviando…" : "Recibir código"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                ¿Sos administrador? <Link to="/admin/login" className="brand-accent-text underline">Ingresá acá</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-6" data-testid="otp-form">
              <div>
                <h1 className="font-head text-2xl font-bold text-slate-900">Verificá tu código</h1>
                <p className="text-sm text-muted-foreground mt-1">Ingresá el código de 6 dígitos enviado a {form.email}.</p>
                {devOtp && <p className="text-xs mt-2 brand-accent-text font-mono">Código (dev): {devOtp}</p>}
              </div>
              <InputOTP maxLength={6} value={codigo} onChange={setCodigo} data-testid="otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="rounded-none w-12 h-14 text-lg" />)}
                </InputOTPGroup>
              </InputOTP>
              <Button type="submit" disabled={loading || codigo.length < 6} data-testid="otp-submit"
                className="w-full rounded-none brand-bg text-white hover:opacity-90 h-11">
                {loading ? "Verificando…" : "Entrar"}
              </Button>
              <button type="button" onClick={() => setStep("form")} className="text-sm text-muted-foreground underline w-full">
                Cambiar email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
